import { Loader2, Sparkles } from 'lucide-react';
import type { CreativeAsset } from '../../../types/metaAdDraft';
import type { UploadedImage } from '../useAssetUpload';
import type { UseAutoThumbnail } from '../useAutoThumbnail';
import { AssetUploader } from './AssetUploader';

// D3.4b — the thumbnail area under a populated VIDEO slot. Wraps the existing
// manual image uploader (its own Replace/Remove = the "Replace" affordance) with
// the auto-extraction states: a "Generating thumbnail…" spinner while the frame
// grab + upload run, the "Auto-generated from first frame" caption + Re-extract
// once applied, and the manual-fallback note when extraction failed. Used by the
// primary ad's feed/story slots (thumbnail optional) and every additional-ad
// video slot (thumbnail required — no auto-extraction server-side on attach).
export function VideoThumbnailField({
  accountId,
  slot,
  autoThumb,
  required,
  onManualChange,
  aspectGuidance,
}: {
  accountId: string;
  slot: CreativeAsset;
  autoThumb: UseAutoThumbnail;
  required: boolean;
  onManualChange: (img: UploadedImage | null) => void;
  aspectGuidance: string;
}) {
  const { status, reExtract, canReExtract } = autoThumb;

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-slate-600">{required ? 'Thumbnail (required)' : 'Custom thumbnail'}</p>
      <p className="text-xs text-slate-400">
        {required
          ? 'Meta requires a thumbnail for video ads. Auto-generated from the first frame on upload.'
          : 'Auto-generated from the first frame on upload; replace it for a different image.'}
      </p>

      {status === 'extracting' ? (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Generating thumbnail…
        </div>
      ) : (
        <>
          {status === 'failed' && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Couldn’t auto-generate — upload a thumbnail{required ? ' (required)' : ''}.
            </p>
          )}
          {status === 'done' && slot.thumbnailUrl && (
            <p className="flex items-center gap-2 text-xs text-slate-500">
              <Sparkles className="h-3.5 w-3.5 text-kyck-600" />
              Auto-generated from first frame
              {canReExtract && (
                <button
                  type="button"
                  onClick={reExtract}
                  className="font-medium text-kyck-700 hover:underline"
                >
                  Re-extract
                </button>
              )}
            </p>
          )}
          <AssetUploader
            kind="image"
            value={
              slot.thumbnailUrl && slot.thumbnailPath
                ? { imageUrl: slot.thumbnailUrl, imagePath: slot.thumbnailPath }
                : null
            }
            onChange={onManualChange}
            accountId={accountId}
            aspectGuidance={aspectGuidance}
          />
        </>
      )}
    </div>
  );
}
